import { AdobeAnalyticsService, CookiesHandler } from 'Services';
import React from 'react';
import { Origin } from 'Constants';

type TrackingEvent =
  | 'clic'
  | 'popin'
  | 'ec_directcall_ciam'
  | 'dmax_directcall_ciam'
  | 'opp_directcall_ciam'
  | 'tunnel-panne_directcall_ciam';

export type Action = 'submit' | 'link' | 'success' | 'failure' | 'popin' | 'timeout' | 'click';
export const customActions: Partial<Record<Stage, Partial<Record<Action, string>>>> = {
  'Registration Page': {
    popin: 'popin_modification_addresse',
    success: 'adresse_formulaire_OK',
    failure: 'changement_adresse_formulaire',
  },
};

export const getCustomAction = (action: Action, stage?: Stage): string | undefined => stage && customActions[stage]?.[action];

const updateDatalayer = (args: Partial<TcVarsData>) => {
  window.tc_vars = { ...window.tc_vars, ...args };
};

const triggerTracking = (event: TrackingEvent, data?: Partial<TcVarsData>, withDirectCall = true) => {
  updateDatalayer({ ...data });
  withDirectCall && window._satellite && window._satellite.track(event);
};

const triggerTrackingPage = (data: Partial<TcVarsData>, withDirectCall: boolean, event: TrackingEvent = 'ec_directcall_ciam') => {
  process.env.REACT_APP_DIRECT_TRACKING === 'true'
    ? AdobeAnalyticsService.track('page_view', data)
    : triggerTracking(event, data, withDirectCall);
};

const triggerTrackingClic = (eventId: string, path: string, event?: React.MouseEvent<HTMLElement>, data?: TcVarsData) => {
  const dataUpdated: Partial<TcVarsData> = {
    ...data,
    event_id: eventId,
    link_name: (event?.target as HTMLElement)?.innerText || '',
    page_url: window.location.href.split('?')[0],
    page_pagename: path,
  };

  process.env.REACT_APP_DIRECT_TRACKING === 'true'
    ? AdobeAnalyticsService.track('clic', dataUpdated)
    : triggerTracking('clic', dataUpdated);
};

const createPageData = (
  stage: Stage,
  path: string,
  templateName: string,
  mobile = false,
  cartWithProducts = false,
): Partial<TcVarsData> => {
  const pageCategories = path.split('/');
  return {
    env_plateforme: getPlatformEnvironment(mobile),
    env_work: getEnvironment(),
    env_template: templateName,
    event_id: buildPageEventId(stage),
    events: buildEvents(stage),
    page_referer: document.referrer,
    page_pagename: path,
    page_url: window.location.href.split('?')[0],
    page_cat1: pageCategories[1] || '',
    page_cat2: pageCategories[2] || '',
    page_cat3: pageCategories[3] || '',
    page_cat4: pageCategories[4] || '',
    product_shoppingtool: localStorage.getItem('TC_SHOPPING_TOOL_DATA') || '',
    user_authent: 'CIAM',
    user_basket_status: cartWithProducts ? '1' : '0',
    user_comparator_status: '0',
    user_email: CookiesHandler.getUserEmail(),
    user_geoloc: CookiesHandler.getUserLocationStatus(),
    user_id: CookiesHandler.getUserId(),
    user_logged: '0',
    user_new_customer: 'non logue',
    user_postalcode: CookiesHandler.getPostalCode(),
    user_wishlist_status: '0',
  };
};

const buildSatelliteTrack = (origin: Origin): TrackingEvent => {
  switch (origin) {
    case Origin.PIPE_DARTY_MAX:
      return 'dmax_directcall_ciam';
    case Origin.PIPE:
    case Origin.PIPE_SLOT_POST_PURCHASE:
    case Origin.PIPE_PSE_POST_PURCHASE:
      return 'opp_directcall_ciam';
    case Origin.BREAKDOWN:
      return 'tunnel-panne_directcall_ciam';
    case Origin.CARE:
    default:
      return 'ec_directcall_ciam';
  }
};

const trackPageData = (stage: Stage, mobile: boolean, cartWithProducts: boolean, origin: Origin) => {
  const path = buildPath(stage, mobile, origin);
  const templateName = buildTemplateName(stage, origin);
  const pageData = createPageData(stage, path, templateName, mobile, cartWithProducts);
  const satelliteTrack = buildSatelliteTrack(origin);
  triggerTrackingPage(pageData, stage !== 'Connexion / Registration Page', satelliteTrack);
};

const buildEvents = (stage: Stage) => {
  switch (stage) {
    case 'Connexion Page':
    case 'Connexion / Registration Page':
      return 'event4,event112';
    case 'Registration Page':
      return 'event5,event113';
    default:
      return '';
  }
};

const buildPageEventId = (stage: Stage) => {
  switch (stage) {
    case 'Connexion Page':
    case 'Connexion / Registration Page':
      return 'identification_ciam';
    case 'Registration Page':
      return 'identification_formulaire_ciam';
    default:
      return '';
  }
};

const buildEventIdClick = (stage: Stage, action: Action | string, origin: Origin) =>
  'CIAM_' + (origin === Origin.PIPE ? 'OPP_' : 'EC_') + buildName(stage) + '_' + action;

const getPlatformEnvironment = (mobile: boolean) => (mobile ? 'mobile' : 'www');

const getEnvironment = () => ('prod' === process.env.REACT_APP_ENVIRONMENT ? 'prod' : 'preprod');

const isPipeOrigin = (origin: Origin) => origin === Origin.PIPE || origin === Origin.PIPE_DARTY_MAX;

const isPostAchatOrigin = (origin: Origin) => origin === Origin.PIPE_SLOT_POST_PURCHASE || origin === Origin.PIPE_PSE_POST_PURCHASE;

const isBreakDownOrigin = (origin: Origin) => origin === Origin.BREAKDOWN;

const getOrigin = (origin: Origin) => {
  if (isPostAchatOrigin(origin)) {
    return 'post-achat';
  } else if (isPipeOrigin(origin)) {
    return 'etape_achat';
  } else if (isBreakDownOrigin(origin)) {
    return 'panne';
  }
  return 'espace_client';
};

const buildPath = (stage: Stage, mobile = false, origin: Origin) =>
  getPlatformEnvironment(mobile) + '/' + getOrigin(origin) + '/' + buildName(stage);

const buildName = (stage: Stage) => (stage ? stage.replaceAll(/( \/ )| /g, '-').toLowerCase() : 'default');

const buildPrefix = (origin: Origin) => {
  switch (origin) {
    case Origin.PIPE:
      return 'funnel_opp';
    case Origin.BREAKDOWN:
      return 'funnel_panne';
    case Origin.PIPE_DARTY_MAX:
      return 'funnel_darty_max';
    case Origin.PIPE_SLOT_POST_PURCHASE:
      return 'funnel_slot_post_purchase';
    case Origin.PIPE_PSE_POST_PURCHASE:
      return 'funnel_pse_post_purchase';
    default:
      return 'myaccount';
  }
};

const buildTemplateName = (stage: Stage, origin: Origin) => {
  const prefix = buildPrefix(origin);

  switch (stage) {
    case 'Connexion Page':
    case 'Connexion / Registration Page':
      return prefix + '_identification';
    case 'Registration Page':
      return prefix + '_form';
    case 'Confirmation Page':
      return prefix + '_confirmation';
    default:
      return prefix + '_other';
  }
};

export const TrackingService = {
  triggerTrackingClic,
  trackPageData,
  buildEventIdClick,
  buildPath,
  buildSatelliteTrack,
};
